import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/lukas/Sites/git/own-packages/react-form-package/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { Form, Field, FieldWrapper, Select, RadioGroup, Button } from '../src';
import Downshift from 'downshift';
import { Playground } from 'docz';
import { Autocomplete } from './components/Autocomplete';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "third-party-components"
    }}>{`Third Party Components`}</h1>
    <blockquote>
      <p parentName="blockquote">{`Working with third party components`}</p>
    </blockquote>
    <p>{`Sometimes you need to work with third party components to make something work properly, e.g. you need an autocompletion. This `}<inlineCode parentName="p">{`react-form-package`}</inlineCode>{` does not provide an autocompletion by default, but luckily you can use third party components within `}<inlineCode parentName="p">{`react-form-package`}</inlineCode>{` and keep all the functionality.`}</p>
    <p>{`To give you an example of how to create a autocompletion form we use `}<a parentName="p" {...{
        "href": "https://github.com/paypal/downshift#readme"
      }}>{`downshift`}</a>{`.`}</p>
    <p><inlineCode parentName="p">{`react-form-package`}</inlineCode>{` has a `}<inlineCode parentName="p">{`<FieldWrapper />`}</inlineCode>{` component. This component exposes four props to the child component: `}<inlineCode parentName="p">{`onFocus`}</inlineCode>{`, `}<inlineCode parentName="p">{`onBlur`}</inlineCode>{`, `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` and `}<inlineCode parentName="p">{`meta`}</inlineCode>{`.`}</p>
    <p>{`This props are functions that takes exactly one argument: `}<inlineCode parentName="p">{`value`}</inlineCode>{`. Which should be a `}<inlineCode parentName="p">{`string`}</inlineCode>{` for input fields or a `}<inlineCode parentName="p">{`boolean`}</inlineCode>{` for a checkbox.`}</p>
    <h2 {...{
      "id": "autocomplete"
    }}>{`Autocomplete`}</h2>
    <p>{`First off, import your components.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import React from 'react';
import Downshift from 'downshift';
import {
  FormControl,
  Label,
} from 'styled-bootstrap-components';
`}</code></pre>
    <p>{`The next step is to create a `}<inlineCode parentName="p">{`Autocomplete`}</inlineCode>{` component.`}</p>
    <p>{`We use the standard example from the `}<a parentName="p" {...{
        "href": "https://github.com/paypal/downshift#readme"
      }}>{`downshift`}</a>{` documentation.`}</p>
    <p>{`We use the exposed function props to change the state of the `}<inlineCode parentName="p">{`<Form />`}</inlineCode>{`. Take a look at the `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` function of the `}<inlineCode parentName="p">{`<Downshift />`}</inlineCode>{` component, or the `}<inlineCode parentName="p">{`onFocus`}</inlineCode>{` and `}<inlineCode parentName="p">{`onBlur`}</inlineCode>{` function on the input component, or the `}<inlineCode parentName="p">{`meta`}</inlineCode>{` data used on the `}<inlineCode parentName="p">{`<FormControl />`}</inlineCode>{` component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// ./Autocomplete.js

const items = [
  { value: 'apple' },
  { value: 'pear' },
  { value: 'orange' },
  { value: 'grape' },
  { value: 'banana' },
];

const Autocomplete = (props) => (
  <Downshift
    // here we are using the onChange function
    onChange={(item) => props.onChange(item.value)}
    itemToString={(item) => (item ? item.value : '')}
  >
    {({
      getInputProps,
      getItemProps,
      getLabelProps,
      getMenuProps,
      isOpen,
      inputValue,
      highlightedIndex,
      selectedItem,
    }) => (
      <div>
        <Label {...getLabelProps()}>Enter a fruit: </Label>
        <FormControl
          {...getInputProps({
            // here we are using the onFocus and onBlur function
            onFocus: (e) => props.onFocus(e.target.value),
            onBlur: (e) => props.onBlur(e.target.value),
            placeholder: 'apple',
          })}
          // here we are using the meta data
          valid={props.meta.touched ? props.meta.valid : undefined}
          invalid={props.meta.touched ? props.meta.invalid : undefined}
        />
        <ul {...getMenuProps()}>
          {isOpen
            ? items
              .filter((item) => !inputValue || item.value.includes(inputValue))
              .map((item, index) => (
                <li
                  {...getItemProps({
                    key: item.value,
                    index,
                    item,
                    style: {
                      backgroundColor:
                        highlightedIndex === index ? 'lightgray' : 'white',
                      fontWeight: selectedItem === item ? 'bold' : 'normal',
                    },
                  })}
                >
                  {item.value}
                </li>
              ))
            : null}
        </ul>
      </div>
    )}
  </Downshift>
);

export { Autocomplete };
`}</code></pre>
    <p>{`Now we have to import all components that we need and use our `}<inlineCode parentName="p">{`<FieldWrapper />`}</inlineCode>{` component inside the `}<inlineCode parentName="p">{`<Form />`}</inlineCode>{` component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import React from 'react';
import { Autocomplete } from './Autocomplete';
import {
  Form,
  FieldWrapper,
  Button,
} from 'react-form-package';
`}</code></pre>
    <p>{`If you visit the focus and blur the input now there will be a error message, telling you that this field is required. If you type something and select a fruit from the autocompletion you will be able to submit the form. If the field was touched and the field is invalid it will use a red border, if the field was touched and the field is valid it will use a green border.`}</p>
    <Playground __position={0} __code={'<Form validate>\n  <div>\n    <FieldWrapper type=\"text\" id=\"autocomplete\" required>\n      <Autocomplete />\n    </FieldWrapper>\n  </div>\n  <div>\n    <Button\n      type=\"submit\"\n      onClick={state => {\n        alert(JSON.stringify(state, null, 2))\n        alert(\'open the console to see the whole state...\')\n        console.log(state)\n      }}\n    >\n      Submit\n    </Button>\n  </div>\n</Form>'} __scope={{
      props,
      DefaultLayout,
      Form,
      Field,
      FieldWrapper,
      Select,
      RadioGroup,
      Button,
      Downshift,
      Playground,
      Autocomplete
    }} mdxType="Playground">
  <Form validate mdxType="Form">
    <div>
    <FieldWrapper type="text" id="autocomplete" required mdxType="FieldWrapper">
      <Autocomplete mdxType="Autocomplete" />
    </FieldWrapper>
    </div>
    <div>
      <Button type="submit" onClick={state => {
            alert(JSON.stringify(state, null, 2));
            alert('open the console to see the whole state...');
            console.log(state);
          }} mdxType="Button">
        Submit
      </Button>
    </div>
  </Form>
    </Playground>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      